import { useId, useState, type ReactNode } from 'react';
import {
  BellRingingIcon,
  CheckCircleIcon,
  PaperPlaneTiltIcon,
  PencilSimpleIcon,
  SpinnerIcon,
} from '@phosphor-icons/react';
import { T, useGT } from 'gt-react';
import type { FeatureInterestKey } from '@erp/shared-types';

import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';
import { useFeatureInterest } from '@/components/feature-preview/useFeatureInterest';

interface NotifyMeCtaProps {
  featureKey: FeatureInterestKey;
  /** Primary button copy — written per feature so the ask stays specific. */
  label: ReactNode;
  /** Reassurance line under the button (no spam, one email, etc.). */
  helper: ReactNode;
  /** Asks what the user would use the feature for, e.g. "What would you hand off first?" */
  notePrompt: ReactNode;
  notePlaceholder: string;
  className?: string;
}

/**
 * The conversion point of a feature-preview page. The form asks what the user
 * would use the feature for up front — that answer is the whole point of the
 * experiment — but the field is optional and submits with the same single
 * click, so someone who just wants the alert isn't blocked by it.
 */
export function NotifyMeCta({
  featureKey,
  label,
  helper,
  notePrompt,
  notePlaceholder,
  className,
}: NotifyMeCtaProps) {
  const gt = useGT();
  // Both the hero and the closing band render this component, so the label's
  // htmlFor needs an id that is unique per instance.
  const noteFieldId = useId();
  const { isRegistered, savedNote, isLoading, isSubmitting, register } =
    useFeatureInterest(featureKey);
  const [note, setNote] = useState('');
  const [editedNote, setEditedNote] = useState('');
  const [isEditingNote, setIsEditingNote] = useState(false);

  async function handleRegister() {
    await register(note);
    setNote('');
  }

  async function handleSendNote() {
    if (!note.trim()) return;
    await register(note);
    setNote('');
  }

  function handleStartEdit() {
    setEditedNote(savedNote ?? '');
    setIsEditingNote(true);
  }

  async function handleSaveEdit() {
    await register(editedNote);
    setIsEditingNote(false);
    setEditedNote('');
  }

  function handleCancelEdit() {
    setIsEditingNote(false);
    setEditedNote('');
  }

  if (isRegistered) {
    return (
      <div
        className={cn(
          'w-full max-w-xl rounded-xl border border-emerald-500/30 bg-emerald-500/5 p-5',
          className,
        )}
      >
        <div className="flex items-start gap-3">
          <CheckCircleIcon
            className="mt-0.5 size-5 shrink-0 text-emerald-600"
            weight="fill"
          />
          <div className="min-w-0">
            <p className="font-semibold">
              <T context="confirmation heading after a user signs up for early access to a feature">
                You&apos;re on the list.
              </T>
            </p>
            <p className="mt-1 text-sm text-muted-foreground">
              <T context="confirmation subtext after signing up for early access to a feature">
                We&apos;ll email you the moment this is switched on for your
                team. Early requests get set up first.
              </T>
            </p>
          </div>
        </div>

        {savedNote && !isEditingNote ? (
          <div className="group relative mt-4 rounded-lg border bg-background/60 p-3">
            <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              <T context="label above the note a user already sent with their early access request">
                What you told us
              </T>
            </p>
            <p className="mt-1 text-sm whitespace-pre-wrap">{savedNote}</p>
            <button
              type="button"
              onClick={handleStartEdit}
              aria-label={gt('Edit your note', {
                context:
                  'aria label for the button that edits the note sent with an early access request',
              })}
              className="absolute top-2 right-2 rounded-md p-1 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100 hover:bg-muted hover:text-foreground focus-visible:opacity-100"
            >
              <PencilSimpleIcon className="size-3.5" />
            </button>
          </div>
        ) : isEditingNote ? (
          <div className="mt-4">
            <Label htmlFor={noteFieldId} className="text-sm font-medium">
              {notePrompt}
            </Label>
            <Textarea
              id={noteFieldId}
              value={editedNote}
              onChange={(event) => setEditedNote(event.target.value)}
              placeholder={notePlaceholder}
              rows={3}
              className="mt-2 bg-background"
              disabled={isSubmitting}
              autoFocus
            />
            <div className="mt-2 flex gap-2">
              <Button
                variant="outline"
                onClick={handleSaveEdit}
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <SpinnerIcon className="animate-spin" />
                ) : (
                  <PaperPlaneTiltIcon weight="fill" />
                )}
                <T context="button that saves an edited note attached to an early access request">
                  Save
                </T>
              </Button>
              <Button
                variant="ghost"
                onClick={handleCancelEdit}
                disabled={isSubmitting}
              >
                <T context="button that cancels editing the note attached to an early access request">
                  Cancel
                </T>
              </Button>
            </div>
          </div>
        ) : (
          <div className="mt-4">
            <Label htmlFor={noteFieldId} className="text-sm font-medium">
              {notePrompt}
            </Label>
            <Textarea
              id={noteFieldId}
              value={note}
              onChange={(event) => setNote(event.target.value)}
              placeholder={notePlaceholder}
              rows={3}
              className="mt-2 bg-background"
              disabled={isSubmitting}
            />
            <Button
              className="mt-2"
              variant="outline"
              onClick={handleSendNote}
              disabled={!note.trim() || isSubmitting}
            >
              {isSubmitting ? (
                <SpinnerIcon className="animate-spin" />
              ) : (
                <PaperPlaneTiltIcon weight="fill" />
              )}
              <T context="button that sends the optional note attached to an early access request">
                Send it over
              </T>
            </Button>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className={cn('w-full max-w-xl', className)}>
      <Label htmlFor={noteFieldId} className="text-sm font-medium">
        {notePrompt}
      </Label>
      <Textarea
        id={noteFieldId}
        value={note}
        onChange={(event) => setNote(event.target.value)}
        placeholder={notePlaceholder}
        rows={3}
        className="mt-2 bg-background"
        disabled={isSubmitting}
      />
      <p className="mt-1.5 text-xs text-muted-foreground">
        <T context="hint under the use case field on a feature preview page, explaining the field is optional">
          Optional — but it&apos;s what decides which use cases get built first.
        </T>
      </p>
      <Button
        size="lg"
        className="mt-3"
        onClick={handleRegister}
        disabled={isLoading || isSubmitting}
        aria-label={gt('Request early access', {
          context:
            'aria label for the primary call to action on a feature preview page',
        })}
      >
        {isSubmitting ? (
          <SpinnerIcon className="animate-spin" />
        ) : (
          <BellRingingIcon weight="fill" />
        )}
        {label}
      </Button>
      <p className="mt-2 text-xs text-muted-foreground">{helper}</p>
    </div>
  );
}
